import {
    Alert,
    AlertTitle,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
} from '@mui/material'
import { useState } from 'react'
import Link from '../../common/util/input/Link'
import { getBoostAction } from '../../helpers/WaxApi'
import { DropPaper } from '../DropPaper'
import { SchemaRequirements } from './SchemaRequirements'
import { TemplateRequirements } from './TemplateRequirements'
import { useSharedState } from '../../waxplorer/Waxplorer'

export const CreateDropDialog = ({
    collectionName,
    createDropDialogOpened,
    setCreateDropDialogOpened,
    attributes,
    blockRules,
    namePattern,
    staticAttributes,
    maxClaimable,
    accountLimit,
    accountLimitCoolDown,
    dropName,
    dropDescription,
    dropStartDate,
    dropStartDateEnabled,
    dropEndDate,
    dropEndDateEnabled,
    dropPriceCurrencyList,
    payoutRecipients,
    recipientSplits,
    authRequired,
    waxRam,
    useExternalMinter,
    handleCallBack,
    previewImage,
}) => {
    const [selectedSchema, setSelectedSchema] = useState(null)
    const [selectedTemplateId, setSelectedTemplateId] = useState(null)

    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState(null)
    const [transactionId, setTransactionId] = useState(null)

    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const getPayoutRecipients = () => {
        const payoutRecipientWithSplits = []
        for (let i = 0; i < payoutRecipients.length; i++) {
            payoutRecipientWithSplits.push({
                account: payoutRecipients[i],
                share: recipientSplits[i],
            })
        }
        return payoutRecipientWithSplits
    }

    // TODO move to DropCreatorUtils
    const isWaxInList = () => {
        for (let i = 0; i < dropPriceCurrencyList.length; i++) {
            if (dropPriceCurrencyList[i].currency === '8,WAX,WAX') {
                return true
            }
        }
        return false
    }

    const getSettlementSymbol = () => {
        return dropPriceCurrencyList[0].price === 0
            ? '0,NULL'
            : isWaxInList()
            ? '8,WAX'
            : dropPriceCurrencyList[0].currency.split(',')[0] +
              ',' +
              dropPriceCurrencyList[0].currency.split(',')[1]
    }

    const getListingPrices = () => {
        const listingPrices = []
        for (const priceCurrency of dropPriceCurrencyList) {
            const price_symbol = priceCurrency.currency.split(',')[2]
            const decimals =
                price_symbol === 'USD'
                    ? 2
                    : priceCurrency.currency.split(',')[0]
            const listing_price =
                priceCurrency.price === 0
                    ? '0 NULL'
                    : `${Number(priceCurrency.price).toFixed(
                          decimals,
                      )} ${price_symbol}`
            listingPrices.push(listing_price)
        }
        return listingPrices
    }

    const create = async () => {
        setIsCreating(true)
        const createDropActions = [getBoostAction(activeUser)]

        let completeNamePattern = namePattern + ' {num}'

        const createDropData = {
            attributes: attributes,
            blacklist: blockRules,
            authorized_account: userName,
            collection_name: collectionName,
            listing_prices: getListingPrices(),
            settlement_symbol: getSettlementSymbol(),
            price_recipients: getPayoutRecipients(),
            auth_required: authRequired,
            is_hidden: false,
            max_claimable: maxClaimable.toString(),
            account_limit: accountLimit.toString(),
            account_limit_cooldown: parseInt(accountLimitCoolDown),
            start_time: dropStartDateEnabled
                ? Math.floor(dropStartDate.getTime() / 1000)
                : 0,
            end_time: dropEndDateEnabled
                ? Math.floor(dropEndDate.getTime() / 1000)
                : 0,
            external_mint: useExternalMinter,
            name_pattern: completeNamePattern,
            schema_name: selectedSchema,
            template_id: selectedTemplateId,
            display_data: JSON.stringify({
                name: dropName,
                description: dropDescription,
                preview_img: previewImage,
            }),
        }

        if (waxRam && parseFloat(waxRam) > 0) {
            createDropActions.push({
                account: 'eosio.token',
                name: 'transfer',
                authorization: [
                    {
                        actor: userName,
                        permission: activeUser.requestPermission
                            ? activeUser.requestPermission
                            : 'active',
                    },
                ],
                data: {
                    from: userName,
                    to: 'nfthivedrops',
                    quantity: `${parseFloat(waxRam).toFixed(8)} WAX`,
                    memo: 'deposit_collection_ram:' + collectionName,
                },
            })
        }

        const createDropAction = {
            account: 'nfthivedrops',
            name: 'createpfp',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: createDropData,
        }

        createDropActions.push(createDropAction)
        let createDropTransferError = null
        let createDropTransactionId = null

        try {
            const createDropResult = await activeUser.session.transact(
                {
                    actions: createDropActions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            createDropTransactionId = createDropResult?.response?.transaction_id
        } catch (e) {
            createDropTransferError = e.message
            setError(e.message)
        }

        if (createDropTransactionId) {
            setTransactionId(createDropTransactionId)
        }
        handleCallBack(
            createDropTransferError === null,
            createDropTransactionId,
        )
        setIsCreating(false)
        return
    }

    return (
        <Dialog
            open={createDropDialogOpened}
            onClose={() => {
                setSelectedSchema(null)
                setSelectedTemplateId(null)
                setIsCreating(false)
                setError(null)
                setTransactionId(null)
                setCreateDropDialogOpened(false)
            }}
            fullWidth
            maxWidth="lg"
        >
            <DropPaper noMargin>
                <div className="text-4xl text-center mb-5">Create Drop</div>
                {isCreating ? (
                    <div className="flex justify-center">
                        <CircularProgress />
                    </div>
                ) : (
                    <>
                        <Collapse in={selectedSchema !== null}>
                            <Link
                                href={`/collection/${collectionName}/${selectedSchema}`}
                                external
                            >
                                <div className="text-primary hover:underline">
                                    Selected Schema:{' '}
                                    <span className="font-mono">
                                        {selectedSchema}
                                    </span>
                                </div>
                            </Link>
                        </Collapse>
                        <Collapse in={selectedTemplateId !== null}>
                            <div className="text-primary hover:underline">
                                <Link
                                    href={`/template/${selectedTemplateId}`}
                                    external
                                >
                                    Selected Template ID:{' '}
                                    <span className="font-mono">
                                        {selectedTemplateId}
                                    </span>
                                </Link>
                            </div>
                        </Collapse>
                        <Collapse in={selectedSchema === null}>
                            <SchemaRequirements
                                collectionName={collectionName}
                                layers={attributes}
                                staticAttributes={staticAttributes}
                                setSelectedSchema={setSelectedSchema}
                            />
                        </Collapse>
                        <Collapse
                            in={
                                selectedSchema !== null &&
                                selectedTemplateId === null
                            }
                        >
                            <TemplateRequirements
                                collectionName={collectionName}
                                schemaName={selectedSchema}
                                staticAttributes={staticAttributes}
                                setSelectedTemplateId={setSelectedTemplateId}
                            />
                        </Collapse>
                        <Collapse
                            in={
                                selectedSchema !== null &&
                                selectedTemplateId !== null
                            }
                        >
                            <div className="mt-2 text-lg font-bold">
                                You are about to create a PFP Drop with the
                                following settings:
                            </div>
                            <div>
                                Drop Name: <b>{dropName}</b>
                            </div>
                            <div>
                                Asset name pattern: <b>{namePattern}</b>
                            </div>
                            {getListingPrices().map((price, index) => (
                                <div id={'price_' + index}>
                                    Price: <b>{price}</b>
                                </div>
                            ))}
                            {dropStartDateEnabled ? (
                                <div>
                                    Start Date:{' '}
                                    <b>{dropStartDate.toString()}</b>
                                </div>
                            ) : null}
                            {dropEndDateEnabled ? (
                                <div>
                                    End Date: <b>{dropEndDate.toString()}</b>
                                </div>
                            ) : null}
                            {authRequired ? (
                                <div className="mt-2">
                                    <Alert severity="warning">
                                        You enabled Drop Restriction, make sure
                                        to create whitelisting rules after
                                        creating up the drop!
                                    </Alert>
                                </div>
                            ) : null}
                            <div className="my-3 flex justify-center">
                                <div className="flex flex-wrap">
                                    <div className="mx-2 w-48">
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => create()}
                                            disabled={transactionId !== null}
                                        >
                                            Create PFP Drop
                                        </Button>
                                    </div>
                                    <div className="mx-2 w-48">
                                        <Button
                                            fullWidth
                                            color="error"
                                            variant="outlined"
                                            onClick={() => {
                                                setSelectedSchema(null)
                                                setSelectedTemplateId(null)
                                                setIsCreating(false)
                                                setError(null)
                                                setTransactionId(null)
                                                setCreateDropDialogOpened(false)
                                            }}
                                        >
                                            {transactionId !== null
                                                ? 'Close'
                                                : 'Cancel'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            {transactionId !== null ? (
                                <div>
                                    <Alert severity="success">
                                        <Link
                                            external={true}
                                            href={
                                                (process.env
                                                    .NEXT_PUBLIC_TESTNET ===
                                                'TRUE'
                                                    ? 'https://testnet.waxblock.io/transaction/'
                                                    : 'https://waxblock.io/transaction/') +
                                                transactionId
                                            }
                                        >
                                            <AlertTitle>Success!</AlertTitle>
                                            Transaction ID: {transactionId}
                                        </Link>
                                    </Alert>
                                </div>
                            ) : error !== null ? (
                                <div>
                                    <Alert severity="error">
                                        <AlertTitle>
                                            An error occured!
                                        </AlertTitle>
                                        {error}
                                    </Alert>
                                </div>
                            ) : null}
                        </Collapse>
                    </>
                )}
            </DropPaper>
        </Dialog>
    )
}
